<template>
  <button
    class="relative group"
    :class="[
      roundedClass,
      buttonTypeClassMap[type],
      sizeClass,
      loading && '!pointer-events-none',
      active ? buttonActiveClassMap[type] : buttonInactiveClassMap[type],
      textColorMap[type],
    ]"
  >
    <div
      v-if="loading"
      class="absolute p-2 w-full h-full flex left-0 top-0 items-center justify-center"
    >
      <StellaIcon :size="fallbackIconSize" icon="spinner" />
    </div>
    <div
      class="flex gap-1.5 items-center justify-center"
      :class="[
        loading && 'opacity-0',
        !loading && 'group-disabled:pointer-events-none',
        type === 'text' && !loading && 'pointer-events-auto',
      ]"
    >
      <StellaIcon
        v-if="icon"
        :size="iconSize ?? fallbackIconSize"
        :icon="icon"
      />
      <StellaText
        v-if="slots.default"
        class="whitespace-nowrap empty:absolute"
        type="Label S"
      >
        <slot />
      </StellaText>
      <StellaIcon
        v-if="iconRight"
        :size="iconRightSize ?? fallbackIconSize"
        :icon="iconRight"
      />
    </div>
  </button>
</template>

<script lang="ts" setup>
import { type StellaIconSize } from '/@/stella/icons/types'
import { type StellaIconId } from './icons/types'
import { computed, useSlots } from 'vue'
import type { StellaButtonSize, StellaButtonType } from './types'

const slots = useSlots()

const {
  type = 'primary',
  size = 'md',
  block = false,
  loading = false,
  active = false,
  icon,
  iconSize,
  iconRight,
  iconRightSize,
} = defineProps<{
  type?: StellaButtonType
  size?: StellaButtonSize
  block?: boolean
  loading?: boolean
  active?: boolean
  icon?: StellaIconId
  iconSize?: StellaIconSize
  iconRight?: StellaIconId
  iconRightSize?: StellaIconSize
}>()

const buttonActiveClassMap: Record<StellaButtonType, string> = {
  primary: 'bg-navy-70',
  outlined: 'bg-navy-10',
  secondary: 'bg-navy-20',
  'ghost-primary': 'bg-navy-20',
  tertiary: 'bg-cream-60',
  green: 'bg-green-30',
  'ghost-secondary': 'bg-cream-60',
  'ghost-green': 'bg-green-30',
  'static-white': 'bg-grey-20',
  ghost: 'bg-grey-80 bg-opacity-40',
  critical: 'bg-red-70',
  success: 'bg-green-70',
  warning: 'bg-yellow-80',
  text: 'bg-transparent',
  'vibrant-primary': 'interactive-vibrant-primary-strongest',
  'vibrant-secondary': 'interactive-vibrant-secondary-strongest',
  orange: 'bg-red-30',
  purple: 'bg-purple-20',
}

const buttonInactiveClassMap: Record<StellaButtonType, string> = {
  primary: 'bg-navy-50',
  outlined: 'bg-transparent',
  secondary: 'bg-navy-10',
  'ghost-primary': 'bg-transparent',
  tertiary: 'bg-cream-40',
  green: 'bg-green-10',
  'ghost-secondary': 'bg-transparent',
  'ghost-green': 'bg-transparent',
  'static-white': 'bg-white',
  ghost: 'bg-transparent',
  critical: 'bg-red-50',
  success: 'bg-green-50',
  warning: 'bg-yellow-50',
  text: 'bg-transparent',
  'vibrant-primary': 'interactive-vibrant-primary-strong',
  'vibrant-secondary': 'interactive-vibrant-secondary-strong',
  orange: 'bg-red-40',
  purple: 'bg-purple-10',
}

const buttonTypeClassMap: Record<StellaButtonType, string> = {
  primary:
    'md:enabled:hover:bg-navy-70 enabled:active:bg-navy-70 disabled:opacity-40',
  outlined:
    'border-navy-50 border-2 md:enabled:hover:bg-navy-10 enabled:active:bg-navy-10 disabled:opacity-40',
  secondary:
    'md:enabled:hover:bg-navy-20 enabled:active:bg-navy-20 disabled:opacity-40',
  'ghost-primary':
    'md:enabled:hover:bg-navy-20 enabled:active:bg-navy-20 disabled:opacity-40',
  tertiary:
    'md:enabled:hover:bg-cream-60 enabled:active:bg-cream-60 disabled:opacity-40',
  green:
    'md:enabled:hover:bg-green-20 enabled:active:bg-green-30 disabled:opacity-40',
  'ghost-secondary':
    'md:enabled:hover:bg-cream-60 enabled:active:bg-cream-60 disabled:opacity-40',
  'ghost-green':
    'md:enabled:hover:bg-green-30 enabled:active:bg-green-30 disabled:opacity-40',
  'static-white':
    'md:enabled:hover:bg-grey-20 enabled:active:bg-grey-20 disabled:opacity-40',
  ghost:
    'md:enabled:hover:bg-grey-80 md:enabled:hover:bg-opacity-20 enabled:active:bg-grey-80 enabled:active:bg-opacity-40 disabled:opacity-40',
  critical:
    'md:enabled:hover:bg-red-70 enabled:active:bg-red-70 disabled:opacity-40',
  success:
    'md:enabled:hover:bg-green-70 enabled:active:bg-green-70 disabled:opacity-40',
  warning:
    'md:enabled:hover:bg-yellow-80 enabled:active:bg-yellow-80 disabled:opacity-40',
  text: 'pointer-events-none hover:underline',
  'vibrant-primary':
    'md:enabled:hover:interactive-vibrant-primary-strongest enabled:active:interactive-vibrant-primary-strongest disabled:opacity-40',
  'vibrant-secondary':
    'md:enabled:hover:interactive-vibrant-secondary-strongest enabled:active:interactive-vibrant-secondary-strongest disabled:opacity-40',
  orange:
    'md:enabled:hover:bg-red-30 enabled:active:bg-red-30 disabled:opacity-40',
  purple:
    'border-navy-60 border-2 md:enabled:hover:bg-purple-20 enabled:active:bg-purple-20 disabled:opacity-40',
}

const textColorMap: Record<StellaButtonType, string> = {
  primary: 'text-white',
  outlined: 'text-navy',
  secondary: 'text-navy',
  'ghost-primary': 'text-black',
  tertiary: 'text-black',
  green: 'text-green-80',
  'ghost-secondary': 'text-black',
  'ghost-green': 'text-black',
  'static-white': 'text-black',
  ghost: 'text-white',
  critical: 'text-white',
  success: 'text-white',
  warning: 'text-black',
  text: 'text-black',
  'vibrant-primary': 'text-black',
  'vibrant-secondary': 'text-black',
  orange: 'text-white',
  purple: 'text-navy-60',
}

const sizeClass = computed(() => {
  if (!slots.default) {
    if (size === 'xs') return 'h-6 w-6'
    else if (size === 'sm') return 'h-8 w-8'
    else if (size === 'lg') return 'h-14 w-14'
    else return 'h-12 w-12'
  } else if (block) {
    if (size === 'xs') return 'w-full h-6'
    else if (size === 'sm') return 'w-full h-8'
    else if (size === 'lg') return 'w-full h-14'
    else return 'w-full px-6 h-12'
  } else {
    if (size === 'xs') return 'w-fit h-6 px-2'
    else if (size === 'sm') return 'w-fit px-2 md:px-4 h-8'
    else if (size === 'lg') return 'w-fit px-8 h-14'
    else return 'w-fit px-6 h-12'
  }
})

const roundedClass = computed(() => {
  return size === 'sm' || size === 'xs' ? 'rounded' : 'rounded-md'
})

const fallbackIconSize = computed(() => {
  if (!slots.default) {
    return size === 'xs' ? 'xs' : 'md'
  }

  switch (size) {
    case 'xs':
    case 'sm':
      return 'xs'
    case 'md':
      return 'sm'
    default:
      return 'md'
  }
})
</script>
